<template>
  <lzd-modal
    title="消息发送"
    :model-value="modelValue"
    style="max-width: 90%"
    :size="700"
    :append-to-body="true"
    :body-style="{
      paddingBottom: '8px',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto'
    }"
    class="flex-modal"
    @update:modelValue="updateModelValue"
    @opened="handleOpen"
  >
    <el-form ref="ruleFormRef" :model="form" :rules="rules">
      <el-form-item :label="toI18n('标题名称')" prop="title">
        <el-input clearable v-model.trim="form.title" placeholder="标题名称" />
      </el-form-item>
      <el-form-item :label="toI18n('发送内容')" prop="content">
        <lzd-editor
          v-model="form.content"
          format="json"
          :type="'richtext'"
          style="width: 100%"
          :hide-border="false"
          :showToolbar="true"
          :excludeKeys="[
            'insertImage',
            'insertLink',
            'group-video',
            'fullScreen'
          ]"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="updateModelValue(false)"> 取消 </el-button>
      <el-button type="primary" :loading="loading" @click="sendMsg">
        发送
      </el-button>
    </template>
  </lzd-modal>
</template>

<script lang="ts" setup>
  import { ref, reactive } from 'vue';
  import { toI18n } from '@/api/bsl/system/i18n';
  import { useFormData } from '@/utils/use-form-data';
  import type { Message } from '@/api/bsl/system/message/model';
  import type { FormInstance, FormRules } from 'element-plus';
  import {
    getAppId,
    getDataScopeId,
    tipsError,
    tipsSuccess
  } from '@/utils/lzd-util';
  import systemMessageApi from '@/api/bsl/system/message';
  import { usePermission } from '@/utils/use-permission';

  const props = defineProps<{
    /** 弹窗是否打开 */
    modelValue: boolean;
    /** 修改回显的数据 */
    data?: string[];
  }>();

  const emit = defineEmits<{
    (e: 'done'): void;
    (e: 'update:modelValue', modelValue: boolean): void;
  }>();

  const { hasRole } = usePermission();
  const loading = ref(false);
  const ruleFormRef = ref<FormInstance>();

  /** 表单数据 */
  const [form, resetFields] = useFormData<Partial<Message>>({
    title: '',
    content: ''
  });

  const rules = reactive<FormRules>({
    title: [
      {
        required: true,
        message: toI18n('请输入标题名称'),
        trigger: ['blur', 'change']
      },
      {
        min: 2,
        max: 100,
        message: toI18n('标题长度需在 2-100 个字符之间'),
        trigger: ['blur', 'change']
      }
    ],
    content: [
      {
        required: true,
        validator: (rule, value, callback) => {
          const pureText = value.replace(/<[^>]+>/g, '').trim();
          if (!pureText) {
            callback(new Error(toI18n('请输入发送内容')));
          } else {
            callback();
          }
        },
        trigger: ['blur', 'change']
      }
    ]
  });

  /** 更新modelValue */
  const updateModelValue = (value: boolean) => {
    emit('update:modelValue', value);
  };

  // 打开弹窗
  const handleOpen = () => {
    resetFields();
  };

  const sendMsg = async () => {
    await ruleFormRef.value?.validate((valid, fields) => {
      if (valid) {
        // 构建消息主体
        let messages: Message[] = [];
        props.data?.forEach((v) => {
          messages.push({
            type: 1,
            title: form.title,
            dataScopeId: getDataScopeId(),
            appId: getAppId(),
            content: form.content,
            recipientUserid: v
          });
        });
        if (hasRole('student')) {
          systemMessageApi
            .studentInsert(messages[0])
            .then(() => {
              loading.value = false;
              emit('done');
              updateModelValue(false);
              tipsSuccess('操作成功');
            })
            .catch((err) => {
              tipsError(err);
            });
        } else {
          systemMessageApi
            .teacherBatchInsert(messages)
            .then(() => {
              loading.value = false;
              emit('done');
              updateModelValue(false);
              tipsSuccess('操作成功');
            })
            .catch((err) => {
              tipsError(err);
            });
        }
      } else {
        console.log('error submit!', fields);
      }
    });
  };
</script>
<style lang="scss" scoped>
  :deep(.lzd-wang-editor) {
    width: 100%;
  }
</style>
